import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  formBuilderApi,
  templateConfigKeys,
  useCarrierForms,
  useCarrierSummaries,
  useFormConfig,
} from '@/features/quote-and-apply-builder';

export function useCompositeTemplatesPage() {
  const { carrierId, formId } = useParams<{ carrierId: string; formId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const carrierIdNum = Number(carrierId);
  const { data: carriers } = useCarrierSummaries();
  const carrier = carriers?.find(c => c.insuranceCarrierId === carrierIdNum);
  const { data: forms } = useCarrierForms(carrierIdNum);
  const form = forms?.find(f => f.id === formId);
  const { data: formConfig } = useFormConfig(formId ?? '');
  const questions = formConfig?.formDefinition?.questions ?? {};

  const {
    data: templateConfigResult,
    isLoading,
    error,
  } = useQuery({
    queryKey: templateConfigKeys.detail(formId ?? ''),
    queryFn: () => formBuilderApi.getTemplateConfigs(formId!),
    enabled: !!formId,
  });

  const configs = templateConfigResult?.configs ?? [];

  const { mutate: deleteConfig, isPending: isDeletePending } = useMutation({
    mutationFn: (id: string) => formBuilderApi.deleteTemplateConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateConfigKeys.detail(formId ?? '') });
    },
  });

  const [configToDelete, setConfigToDelete] = useState<PolicyApplicationTemplateConfiguration | null>(null);

  const handleDeleteClick = useCallback(
    (config: PolicyApplicationTemplateConfiguration) => {
      setConfigToDelete(config);
    },
    [],
  );

  const handleConfirmDelete = useCallback(() => {
    if (!configToDelete) return;

    deleteConfig(configToDelete.id, {
      onSuccess: () => {
        toast.success(`Template "${configToDelete.templateName}" has been deleted`);
        setConfigToDelete(null);
      },
      onError: err => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete template configuration');
        setConfigToDelete(null);
      },
    });
  }, [configToDelete, deleteConfig]);

  const handleDismissDelete = useCallback((open: boolean) => {
    if (!open) setConfigToDelete(null);
  }, []);

  const navigateBack = useCallback(
    () => navigate(`/wizard/form-builder/${carrierId}`),
    [navigate, carrierId],
  );

  const isEmptyState = !isLoading && !error && configs.length === 0;
  const isDataLoaded = !isLoading && !error && configs.length > 0;

  return {
    carrier,
    carrierId,
    formId,
    form,
    questions,
    configs,
    isLoading,
    error,
    isDeletePending,
    configToDelete,
    isEmptyState,
    isDataLoaded,
    handleDeleteClick,
    handleConfirmDelete,
    handleDismissDelete,
    navigateBack,
  };
}
